import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import client from '../../api/client';
import PreferredTimePicker from './PreferredTimePicker';
import {
  buildPreferredDateTimeIndia,
  fromIndiaTimeTo12HourParts,
  getEarliestAllowedIndia,
  getNowInIndia,
  is24HourTimeBefore,
  to24HourTimeString,
  toIndiaApiIsoString,
  toIndiaDateInputValue,
  toIndiaTimeInputValue,
} from '../../Utils/indiaTime';
import { isDuplicateVisitSlot, parseVisitDescription } from '../../Utils/patientVisits';

const MIN_BUFFER_HOURS = 12;

const EMPTY_FORM = {
  serviceTypeId: '',
  preferredDate: '',
  timeHour: '',
  timeMinute: '',
  timePeriod: 'AM',
  symptoms: '',
  additionalNotes: '',
};

function getTime24FromForm({ timeHour, timeMinute, timePeriod }) {
  return to24HourTimeString(timeHour, timeMinute, timePeriod);
}

function NewRequestModal({
  show,
  onHide,
  patientId,
  onSuccess,
  existingVisits = [],
  editVisit = null,
}) {
  const isEditMode = Boolean(editVisit?.id);

  const [serviceTypes, setServiceTypes] = useState([]);
  const [isLoadingServices, setIsLoadingServices] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);

  useEffect(() => {
    if (!show) return;

    const loadServiceTypes = async () => {
      setIsLoadingServices(true);
      try {
        const response = await client.get('/api/service-types');
        const { success, data, message } = response.data || {};
        if (success && Array.isArray(data)) {
          setServiceTypes(data);
        } else {
          toast.error(message || 'Unable to load service types.');
        }
      } catch (error) {
        const apiMessage = error?.response?.data?.message;
        toast.error(apiMessage || 'Unable to load service types.');
      } finally {
        setIsLoadingServices(false);
      }
    };

    loadServiceTypes();
  }, [show]);

  useEffect(() => {
    if (!show) {
      setFormData(EMPTY_FORM);
      return;
    }

    if (editVisit) {
      const { symptoms, additionalNotes } = parseVisitDescription(editVisit.description);
      const preferred = editVisit.preferredDateTime
        ? new Date(editVisit.preferredDateTime)
        : null;

      const timeParts =
        preferred && !Number.isNaN(preferred.getTime())
          ? fromIndiaTimeTo12HourParts(preferred)
          : { hour: '', minute: '', period: 'AM' };

      setFormData({
        serviceTypeId: String(editVisit.serviceTypeId ?? ''),
        preferredDate:
          preferred && !Number.isNaN(preferred.getTime())
            ? toIndiaDateInputValue(preferred)
            : '',
        timeHour: timeParts.hour,
        timeMinute: timeParts.minute,
        timePeriod: timeParts.period,
        symptoms,
        additionalNotes: additionalNotes || editVisit.additionalNotes || '',
      });
      return;
    }

    setFormData(EMPTY_FORM);
  }, [show, editVisit]);

  const selectedService = serviceTypes.find(
    (s) => String(s.idServiceType) === String(formData.serviceTypeId),
  );
  const rate = selectedService?.defaultRateCustomer ?? 0;
  const showRate = rate > 0;
  const showOthersNotes =
    selectedService?.serviceName?.toLowerCase().includes('other') ?? false;

  const symptomsCount = formData.symptoms.length;
  const notesCount = formData.additionalNotes.length;

  const earliestAllowed = useMemo(
    () => (show ? getEarliestAllowedIndia(MIN_BUFFER_HOURS) : getNowInIndia()),
    [show],
  );
  const minDate = toIndiaDateInputValue(earliestAllowed);

  const getMinTime24ForDate = (dateStr) => {
    if (!dateStr || dateStr !== minDate) return null;
    return toIndiaTimeInputValue(earliestAllowed);
  };

  const minTime24 = getMinTime24ForDate(formData.preferredDate);
  const selectedTime24 = getTime24FromForm(formData);
  const isTimeComplete = Boolean(formData.timeHour && formData.timeMinute && formData.timePeriod);

  const earliestTimeLabel = useMemo(() => {
    if (!minTime24 || formData.preferredDate !== minDate) return null;
    const parts = fromIndiaTimeTo12HourParts(
      buildPreferredDateTimeIndia(formData.preferredDate, minTime24),
    );
    return `${parts.hour}:${parts.minute} ${parts.period}`;
  }, [minTime24, formData.preferredDate, minDate]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'preferredDate') {
      setFormData((prev) => {
        const next = { ...prev, preferredDate: value };
        const minForDay = getMinTime24ForDate(value);
        const currentTime = getTime24FromForm(prev);
        if (minForDay && currentTime && is24HourTimeBefore(currentTime, minForDay)) {
          next.timeHour = '';
          next.timeMinute = '';
          next.timePeriod = 'AM';
        }
        return next;
      });
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleTimeChange = ({ hour, minute, period }) => {
    setFormData((prev) => ({
      ...prev,
      timeHour: hour,
      timeMinute: minute,
      timePeriod: period,
    }));
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
  };

  const handleClose = () => {
    if (isSubmitting) return;
    resetForm();
    onHide();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!patientId) {
      toast.error('Patient session not found. Please sign in again.');
      return;
    }

    if (!formData.serviceTypeId) {
      toast.error('Please select a service type.');
      return;
    }
    if (!formData.preferredDate || !isTimeComplete) {
      toast.error('Please select preferred date and time.');
      return;
    }

    const preferredTime24 = getTime24FromForm(formData);
    if (!preferredTime24) {
      toast.error('Please select a valid preferred time.');
      return;
    }

    if (minTime24 && is24HourTimeBefore(preferredTime24, minTime24)) {
      toast.error('Visits can be scheduled at least 12 hours from now.');
      return;
    }

    if (formData.symptoms.trim().length < 5) {
      toast.error('Please describe your symptoms (min 5 characters).');
      return;
    }

    const preferredDateTime = buildPreferredDateTimeIndia(
      formData.preferredDate,
      preferredTime24,
    );
    if (Number.isNaN(preferredDateTime.getTime())) {
      toast.error('Invalid preferred date or time.');
      return;
    }

    if (preferredDateTime.getTime() < earliestAllowed.getTime()) {
      toast.error('Visits can be scheduled at least 12 hours from now.');
      return;
    }

    if (
      isDuplicateVisitSlot(existingVisits, {
        serviceTypeId: formData.serviceTypeId,
        preferredDateTime,
        excludeVisitId: isEditMode ? editVisit.id : null,
      })
    ) {
      toast.error(
        'You already have a visit request for this service at the selected date and time.',
      );
      return;
    }

    const payload = {
      PatientID: patientId,
      ServiceTypeID: parseInt(formData.serviceTypeId, 10),
      PreferredDateTime: toIndiaApiIsoString(preferredDateTime),
      Description: formData.symptoms.trim(),
      AdditionalNotes: formData.additionalNotes.trim() || null,
    };

    setIsSubmitting(true);
    try {
      const response = isEditMode
        ? await client.put('/api/visit-requests', {
            IdVisitRequest: editVisit.id,
            ...payload,
          })
        : await client.post('/api/visit-requests', payload);
      const { success, message } = response.data || {};

      if (!success) {
        toast.error(
          message || (isEditMode ? 'Failed to update visit request.' : 'Failed to submit visit request.'),
        );
        return;
      }

      toast.success(
        message ||
          (isEditMode ? 'Visit request updated successfully!' : 'Visit request submitted successfully!'),
      );
      resetForm();
      onHide();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      const apiMessage = error?.response?.data?.message;
      toast.error(
        apiMessage ||
          (isEditMode ? 'Failed to update visit request.' : 'Failed to submit visit request.'),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="lg"
      scrollable
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>{isEditMode ? 'Edit Visit Request' : 'New Visit Request'}</Modal.Title>
      </Modal.Header>
      <form onSubmit={handleSubmit}>
        <Modal.Body>
          {isEditMode && editVisit?.bookingNumber && (
            <p className="text-muted small mb-3">
              Booking: <strong>{editVisit.bookingNumber}</strong>
            </p>
          )}
          {isLoadingServices ? (
            <p className="text-muted text-center py-3">Loading service types...</p>
          ) : (
            <div className="row m-0">
              <div className="col-12 p-2">
                <label htmlFor="serviceTypeId" className="form-label mb-1">
                  Service Type <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  id="serviceTypeId"
                  name="serviceTypeId"
                  value={formData.serviceTypeId}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting || serviceTypes.length === 0}
                >
                  <option value="">Select service type</option>
                  {serviceTypes.map((opt) => (
                    <option key={opt.idServiceType} value={opt.idServiceType}>
                      {opt.serviceName}
                      {opt.defaultRateCustomer > 0
                        ? ` – ₹${Number(opt.defaultRateCustomer).toLocaleString('en-IN')}`
                        : ''}
                    </option>
                  ))}
                </select>
              </div>

              {showRate && (
                <div className="col-md-6 p-2">
                  <label className="form-label mb-1">Consultation Rate</label>
                  <input
                    type="text"
                    className="form-control form-control-display-only"
                    value={`₹${Number(rate).toLocaleString('en-IN')}`}
                    readOnly
                    tabIndex={-1}
                  />
                </div>
              )}

              <div className="col-12 p-2">
                <small className="form-text text-muted">
                  Visits can be scheduled at least 12 hours from now.
                </small>
              </div>

              <div className="col-md-6 p-2">
                <label htmlFor="preferredDate" className="form-label mb-1">
                  Preferred Date <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  className="form-control"
                  id="preferredDate"
                  name="preferredDate"
                  value={formData.preferredDate}
                  onChange={handleChange}
                  min={minDate}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="col-md-6 p-2">
                <label className="form-label mb-1">
                  Preferred Time (IST 🇮🇳) <span className="text-danger">*</span>
                </label>
                <PreferredTimePicker
                  hour={formData.timeHour}
                  minute={formData.timeMinute}
                  period={formData.timePeriod}
                  onChange={handleTimeChange}
                  disabled={isSubmitting || !formData.preferredDate}
                  required
                />
                {earliestTimeLabel && (
                  <small className="form-text text-muted d-block mt-1">
                    Earliest on this date: {earliestTimeLabel}
                  </small>
                )}
                {selectedTime24 && minTime24 && is24HourTimeBefore(selectedTime24, minTime24) && (
                  <small className="form-text text-danger d-block mt-1">
                    Selected time is too soon. Choose a later time.
                  </small>
                )}
              </div>

              <div className="col-12 p-2">
                <label htmlFor="symptoms" className="form-label mb-1">
                  Symptoms / Description <span className="text-danger">*</span>
                </label>
                <textarea
                  className="form-control"
                  id="symptoms"
                  name="symptoms"
                  rows="3"
                  value={formData.symptoms}
                  onChange={handleChange}
                  placeholder="Describe your symptoms or reason for consultation..."
                  maxLength="500"
                  required
                  disabled={isSubmitting}
                />
                <small className="form-text text-muted d-block text-end">
                  {symptomsCount}/500 characters
                </small>
              </div>

              {showOthersNotes && (
                <div className="col-12 p-2">
                  <label htmlFor="additionalNotes" className="form-label mb-1">
                    Additional Notes{' '}
                    <span className="text-muted fw-normal">(Optional – for Others service type)</span>
                  </label>
                  <textarea
                    className="form-control"
                    id="additionalNotes"
                    name="additionalNotes"
                    rows="3"
                    value={formData.additionalNotes}
                    onChange={handleChange}
                    placeholder="Any additional information or specific requirements..."
                    maxLength="300"
                    disabled={isSubmitting}
                  />
                  <small className="form-text text-muted d-block text-end">
                    {notesCount}/300 characters
                  </small>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn cancel-btn"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn login-submit-btn"
            disabled={isSubmitting || isLoadingServices}
          >
            {isSubmitting
              ? isEditMode
                ? 'Updating...'
                : 'Submitting...'
              : isEditMode
                ? 'Update Request'
                : 'Submit Request'}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}

export default NewRequestModal;
